import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Collapse,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import ScheduleIcon from '@material-ui/icons/Schedule';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import {
  AuditFinding,
  AuditDomainLabel,
  AuditDomain,
  FindingSeverity,
  FindingStatus,
} from '../../types';
import { auditService } from '../../services/auditService';

const useStyles = makeStyles(theme => ({
  filterBar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  filterField: {
    minWidth: 160,
  },
  summaryCards: {
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  summaryValue: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
  },
  summaryLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  severityCritical: { borderLeft: `4px solid #d32f2f` },
  severityHigh: { borderLeft: `4px solid #f44336` },
  severityMedium: { borderLeft: `4px solid #ff9800` },
  severityLow: { borderLeft: `4px solid #2196f3` },
  severityInfo: { borderLeft: `4px solid #9e9e9e` },
  expandedRow: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[50],
  },
  detailGrid: {
    padding: theme.spacing(2),
  },
  detailLabel: {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: '0.85rem',
    marginBottom: theme.spacing(1.5),
  },
  chipArray: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
}));

const severityColors: Record<FindingSeverity, string> = {
  critical: '#d32f2f',
  high: '#f44336',
  medium: '#ff9800',
  low: '#2196f3',
  informational: '#9e9e9e',
};

// Maps a finding severity to its corresponding style key in useStyles.
// 'informational' is special-cased because the generated class name
// (`severityInformational`) doesn't match the defined style key (`severityInfo`).
const severityClassNames: Record<FindingSeverity, string> = {
  critical: 'severityCritical',
  high: 'severityHigh',
  medium: 'severityMedium',
  low: 'severityLow',
  informational: 'severityInfo',
};

const statusColors: Record<FindingStatus, string> = {
  open: '#f44336',
  'in-progress': '#ff9800',
  remediated: '#4caf50',
  'accepted-risk': '#9c27b0',
  closed: '#4caf50',
};

const getSeverityIcon = (severity: FindingSeverity) => {
  switch (severity) {
    case 'critical':
      return <ErrorIcon style={{ color: '#d32f2f', fontSize: 18 }} />;
    case 'high':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />;
    case 'medium':
      return <WarningIcon style={{ color: '#ff9800', fontSize: 18 }} />;
    case 'low':
      return <InfoIcon style={{ color: '#2196f3', fontSize: 18 }} />;
    default:
      return <InfoIcon style={{ color: '#9e9e9e', fontSize: 18 }} />;
  }
};

const FindingRow = ({ finding }: { finding: AuditFinding }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const severityClass = severityClassNames[
    finding.severity
  ] as keyof typeof classes;

  return (
    <>
      <TableRow
        className={classes[severityClass] as string}
        hover
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        <TableCell padding="checkbox">
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <strong>{finding.id}</strong>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            {getSeverityIcon(finding.severity)}
            <Chip
              label={finding.severity}
              size="small"
              style={{
                backgroundColor: severityColors[finding.severity],
                color: '#fff',
                fontSize: '0.7rem',
                height: 20,
              }}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {finding.title}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption">
            {AuditDomainLabel[finding.domain]}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={finding.status}
            size="small"
            style={{
              backgroundColor: statusColors[finding.status],
              color: '#fff',
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption">{finding.owner}</Typography>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <ScheduleIcon style={{ fontSize: 14 }} />
            <Typography variant="caption">{finding.targetDate}</Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div className={classes.detailGrid}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography className={classes.detailLabel}>
                    Description
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.description}
                  </Typography>

                  <Typography className={classes.detailLabel}>
                    Root Cause
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.rootCause}
                  </Typography>

                  <Typography className={classes.detailLabel}>
                    Recommendation
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.recommendation}
                  </Typography>

                  {finding.remediationPlan && (
                    <>
                      <Typography className={classes.detailLabel}>
                        Remediation Plan
                      </Typography>
                      <Typography className={classes.detailValue}>
                        {finding.remediationPlan}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography className={classes.detailLabel}>
                    Control Reference
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.controlRef}
                  </Typography>

                  <Typography className={classes.detailLabel}>
                    Category
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.category}
                  </Typography>

                  <Typography className={classes.detailLabel}>
                    Assigned To
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.assignedTo}
                  </Typography>

                  <Typography className={classes.detailLabel}>
                    Raised Date
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {finding.raisedDate}
                  </Typography>

                  {finding.affectedSystems.length > 0 && (
                    <>
                      <Typography className={classes.detailLabel}>
                        Affected Systems
                      </Typography>
                      <div className={classes.chipArray}>
                        {finding.affectedSystems.map(s => (
                          <Chip
                            key={s}
                            label={s}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {finding.evidence.length > 0 && (
                    <>
                      <Typography className={classes.detailLabel}>
                        Evidence
                      </Typography>
                      <div className={classes.chipArray}>
                        {finding.evidence.map(e => (
                          <Chip
                            key={e}
                            label={e}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {finding.relatedFindings.length > 0 && (
                    <>
                      <Typography className={classes.detailLabel}>
                        Related Findings
                      </Typography>
                      <div className={classes.chipArray}>
                        {finding.relatedFindings.map(rf => (
                          <Chip
                            key={rf}
                            label={rf}
                            size="small"
                            color="secondary"
                          />
                        ))}
                      </div>
                    </>
                  )}
                </Grid>
              </Grid>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const FindingsView = () => {
  const classes = useStyles();
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');

  useEffect(() => {
    auditService.getFindings().then(f => {
      setFindings(f);
      setLoading(false);
    });
  }, []);

  if (loading) return <LinearProgress />;

  const filtered = findings.filter(f => {
    if (severityFilter !== 'all' && f.severity !== severityFilter) return false;
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    if (domainFilter !== 'all' && f.domain !== domainFilter) return false;
    return true;
  });

  const critical = findings.filter(f => f.severity === 'critical').length;
  const high = findings.filter(f => f.severity === 'high').length;
  const open = findings.filter(f => f.status === 'open').length;
  const inProgress = findings.filter(f => f.status === 'in-progress').length;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Audit Findings Register
      </Typography>

      {/* Summary cards */}
      <Grid container spacing={2} className={classes.summaryCards}>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#f44336' }}
            >
              {critical}
            </Typography>
            <Typography className={classes.summaryLabel}>Critical</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#ff5722' }}
            >
              {high}
            </Typography>
            <Typography className={classes.summaryLabel}>High</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#f44336' }}
            >
              {open}
            </Typography>
            <Typography className={classes.summaryLabel}>Open</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#ff9800' }}
            >
              {inProgress}
            </Typography>
            <Typography className={classes.summaryLabel}>
              In Progress
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <div className={classes.filterBar}>
        <TextField
          select
          label="Severity"
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.filterField}
        >
          <MenuItem value="all">All Severities</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="informational">Informational</MenuItem>
        </TextField>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.filterField}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="in-progress">In Progress</MenuItem>
          <MenuItem value="remediated">Remediated</MenuItem>
          <MenuItem value="accepted-risk">Accepted Risk</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </TextField>
        <TextField
          select
          label="Domain"
          value={domainFilter}
          onChange={e => setDomainFilter(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.filterField}
          style={{ minWidth: 220 }}
        >
          <MenuItem value="all">All Domains</MenuItem>
          {Object.values(AuditDomain).map(d => (
            <MenuItem key={d} value={d}>
              {AuditDomainLabel[d]}
            </MenuItem>
          ))}
        </TextField>
        <Typography
          variant="body2"
          style={{ alignSelf: 'center', marginLeft: 'auto' }}
        >
          Showing {filtered.length} of {findings.length} findings
        </Typography>
      </div>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>ID</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Finding</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Target Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(f => (
              <FindingRow key={f.id} finding={f} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
